import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { AppState } from "@auth0/auth0-react";
import { getProfile } from "../../services/users.api";
import { useCompanyStoreActions, useCompanyStore } from "../../states/store/Company";
import { useCompanyAccessStore } from "../../states/store/CompanyAccess";
import { AuthContext, AuthType } from "./authContext/AuthContext";
import { useGetCompanyAccesses } from "@/common/hooks";
import { useAppStore } from "@/common/states/store";

export const AuthProvider = (props: PropsWithChildren) => {
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();
  // const [profile, setProfile] = useState<User>({});
  const [authReady, setAuthReady] = useState(false);
  const [tokenReady, setTokenReady] = useState(false);
  const { setAllSelection, syncAvailableCompanies } = useCompanyStoreActions();
  const { removeCompanyAccess, addCompanyAccess } = useCompanyAccessStore();

  const getToken = useCallback(getAccessTokenSilently, [
    getAccessTokenSilently,
  ]);
  const email =
    isAuthenticated && authReady && user?.email ? user.email : undefined;
  const { data: companiesAccesses } = useGetCompanyAccesses(email);
  const { profile, setProfile } = useAppStore((state) => state.auth);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      setTokenReady(false);
      setAuthReady(true);

      if ("z__z" in window) {
        delete window["z__z"];
      }
    }
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    let isMounted = true;

    async function bootstrapToken() {
      try {
        const token = await getAccessTokenSilently();

        if (!isMounted) {
          return;
        }

        window["z__z"] = () => token;
        setTokenReady(true);
      } catch (err) {
        console.error("Erro ao obter token de acesso:", err);

        if (!isMounted) {
          return;
        }

        setTokenReady(false);
        setAuthReady(true);
      }
    }

    bootstrapToken();

    return () => {
      isMounted = false;
    };
  }, [getAccessTokenSilently, isAuthenticated, user]);

  useEffect(() => {
    if (!user || !tokenReady) return;

    async function getUserProfile() {
      try {
        const data = await getProfile();
        if (data) {
          setProfile(data);
          setAuthReady(true);
        }
      } catch (err) {
        console.error("Erro ao buscar perfil:", err);
        setAuthReady(true);
      }
    }

    getUserProfile();
  }, [isAuthenticated, setProfile, tokenReady, user]);
  useEffect(() => {
    if (!authReady || !isAuthenticated || !tokenReady) return;

    const runCacheSync = async () => {
      const cacheKey = "all_active_companies_cache";

      if (!localStorage.getItem(cacheKey)) {
        await syncAvailableCompanies();
      }
    };

    runCacheSync();
  }, [authReady, isAuthenticated, syncAvailableCompanies, tokenReady]);
  useEffect(() => {
    if (!authReady || !isAuthenticated || !tokenReady || !companiesAccesses) return;

    if (!localStorage.getItem("companiesAccesses")) {
      addCompanyAccess(companiesAccesses);
    }
  }, [authReady, companiesAccesses, addCompanyAccess, isAuthenticated, tokenReady]);

  const auth = useMemo(() => {
    if (!isAuthenticated) return null;
    const mergedUser = {
      ...user,
      ...(profile?.userInfo || {}),
    };

    return {
      user: mergedUser,
      getToken,
    };
  }, [isAuthenticated, user, profile, getToken]);

  const handleLogin = (returnTo = "/") => {
    loginWithRedirect({
      appState: {
        returnTo,
      } as AppState & { returnTo: string },
    });
  };

  const handleLogout = () => {

    setAllSelection();
    removeCompanyAccess();
    setTokenReady(false);

    sessionStorage.clear();

    localStorage.clear();

    useCompanyStore.setState({ availableCompanies: [] });

    setProfile(null);

    if ("z__z" in window) {
      delete window["z__z"];
    }

    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <AuthContext.Provider
      value={{
        auth: auth as AuthType,
        login: handleLogin,
        logout: handleLogout,
        authReady,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};
